import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface RagnarMarkdownProps {
  allowedElements?: string[];
  disallowedElements?: string[];
  unwrapDisallowed?: boolean;
  remarkPlugins?: unknown[];
  rehypePlugins?: unknown[];
  children?: React.ReactNode;
  components?: Record<string, React.ComponentType<any>>;
  [key: string]: unknown;
}

// Helper function to ensure remark-gfm is always included
// NOTE: We ensure remark-math runs BEFORE remark-gfm as a best practice.
// Testing showed both orders produce identical results, but this ordering follows
// the general principle that syntax-specific plugins (math) should run before
// feature plugins (GFM) to avoid potential conflicts.
// So we place remark-math plugins first, then remark-gfm, then other plugins
const ensureRemarkGfm = (plugins?: unknown[]): unknown[] => {
  const pluginsList = plugins ? [...plugins] : [];

  // Separate math plugins (remark-math) from other plugins
  // Ensure remark-math runs before remark-gfm (best practice)
  const mathPlugins: unknown[] = [];
  const otherPlugins: unknown[] = [];

  pluginsList.forEach((plugin) => {
    // Check if this is remark-math (by checking the function name)
    const pluginName = (plugin as any)?.name || '';
    if (pluginName === 'math' || String(plugin).includes('math')) {
      mathPlugins.push(plugin);
    } else {
      // Filter out remark-gfm if it exists (handles both direct and array-wrapped)
      if (Array.isArray(plugin)) {
        if (plugin[0] !== remarkGfm) {
          otherPlugins.push(plugin);
        }
      } else if (plugin !== remarkGfm) {
        otherPlugins.push(plugin);
      }
    }
  });

  // Order: math plugins first, then remark-gfm, then other plugins
  return [...mathPlugins, remarkGfm, ...otherPlugins];
};

// Custom urlTransform to handle javascript: URLs for backward compatibility
// react-markdown v10 sanitizes javascript: URLs by default, but we need to preserve javascript:void(0)
const urlTransform = (url: string): string => {
  // If url is empty (sanitized javascript: URL), return javascript:void(0) for backward compatibility
  if (url === '') {
    // eslint-disable-next-line no-script-url
    return 'javascript:void(0)';
  }
  // Allow safe URLs: http:, https:, mailto:, tel:, absolute paths, and hashes
  if (
    url.startsWith('http:') ||
    url.startsWith('https:') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('/') ||
    url.startsWith('#')
  ) {
    return url;
  }
  // Block other dangerous protocols
  // eslint-disable-next-line no-script-url
  return 'javascript:void(0)';
};

const RagnarMarkdown = ({
  allowedElements,
  disallowedElements,
  unwrapDisallowed,
  remarkPlugins,
  rehypePlugins,
  children,
  components,
  ...props
}: RagnarMarkdownProps) => {
  // Special case: if allowedElements is empty array and unwrapDisallowed is not true,
  // return raw text (for backward compatibility with v4 behavior when only 'text' is allowed)
  // This handles the case where Markdown component passes allowedElements=[] when only 'text' is allowed
  const shouldReturnRawText = React.useMemo(() => {
    return (
      Array.isArray(allowedElements) &&
      allowedElements.length === 0 &&
      unwrapDisallowed !== true &&
      typeof children === 'string'
    );
  }, [allowedElements, unwrapDisallowed, children]);

  // Add remark-gfm for GFM features (strikethrough, tables)
  // Always enable GFM unless explicitly disabled via remarkPlugins
  const finalPlugins = React.useMemo(
    () => ensureRemarkGfm(remarkPlugins),
    [remarkPlugins],
  );

  if (shouldReturnRawText) {
    return children as React.ReactElement;
  }

  // Build props for ReactMarkdown
  const reactMarkdownProps: Record<string, unknown> = {
    ...props,
    urlTransform,
  };

  if (finalPlugins.length > 0) {
    reactMarkdownProps.remarkPlugins = finalPlugins;
  }

  if (rehypePlugins !== undefined && rehypePlugins.length > 0) {
    reactMarkdownProps.rehypePlugins = rehypePlugins;
  }

  if (allowedElements !== undefined) {
    reactMarkdownProps.allowedElements = allowedElements;
  }

  if (disallowedElements !== undefined) {
    reactMarkdownProps.disallowedElements = disallowedElements;
  }

  if (unwrapDisallowed !== undefined) {
    reactMarkdownProps.unwrapDisallowed = unwrapDisallowed;
  }

  if (components) {
    reactMarkdownProps.components = components;
  }

  if (children) {
    reactMarkdownProps.children = children;
  }

  return <ReactMarkdown {...reactMarkdownProps} />;
};

export default RagnarMarkdown;
